import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useGlobalState } from '../contexts/GlobalStateContext';

export interface PresetConfig {
  socialAccountIds: string[];
}

export interface Preset {
  id: string;
  name: string;
  config: PresetConfig;
}

const QUERY_KEY = 'user-presets';

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

export function usePresets() {
  const queryClient = useQueryClient();
  const token = window.localStorage.getItem('token');
  const { localStorage: globalState, updateLocalStorage } = useGlobalState();

  const headers = { Authorization: `Bearer ${token}` };

  const { data: presets = [], isLoading } = useQuery<Preset[]>({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/users/presets`, { headers });
      return (res.data.presets || []) as Preset[];
    },
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async (newPresets: Preset[]) => {
      await axios.put(`${API_BASE_URL}/users/presets`, { presets: newPresets }, { headers });
      return newPresets;
    },
    onMutate: async (newPresets) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      const previous = queryClient.getQueryData<Preset[]>([QUERY_KEY]);
      queryClient.setQueryData([QUERY_KEY], newPresets);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([QUERY_KEY], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const savePresets = (newPresets: Preset[]) => {
    mutation.mutate(newPresets);
  };

  const applyPreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      updateLocalStorage({ selectedPlatforms: preset.config.socialAccountIds });
    }
  };

  const saveCurrentAsPreset = (name: string) => {
    if (presets.length >= 10) return;
    const currentSelection = globalState.selectedPlatforms || [];
    const newPreset: Preset = {
      id: generateId(),
      name: name.trim().substring(0, 50),
      config: {
        socialAccountIds: [...currentSelection],
      },
    };
    savePresets([...presets, newPreset]);
  };

  const deletePreset = (presetId: string) => {
    savePresets(presets.filter(p => p.id !== presetId));
  };

  const renamePreset = (presetId: string, newName: string) => {
    savePresets(
      presets.map(p =>
        p.id === presetId ? { ...p, name: newName.trim().substring(0, 50) } : p
      )
    );
  };

  return {
    presets,
    isLoading,
    applyPreset,
    saveCurrentAsPreset,
    deletePreset,
    renamePreset,
    canSaveMore: presets.length < 10,
  };
}
